import { Button } from "@kontaktfix/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LogoMark } from "@/components/logo";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";

/** Opened from the unsubscribe link in an email. No sign-in: the link carries a signed token. */
export function UnsubscribePage() {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, isError } = useQuery({
    queryKey: ["unsubscribe", token],
    queryFn: () => api.get<{ category: string }>(`/api/unsubscribe?token=${encodeURIComponent(token)}`),
    retry: false,
  });

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/api/unsubscribe?token=${encodeURIComponent(token)}`);
      setDone(true);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const category = data ? t(`notifications.${data.category}.title`) : "";
  return (
    <div className="flex min-h-screen items-center justify-center bg-subtle px-4 py-10">
      <div className="w-full max-w-[420px] rounded-lg border border-border bg-bg p-6 text-center shadow-md">
        <div className="flex justify-center">
          <LogoMark size={36} />
        </div>
        <h1 className="mt-4 text-lg font-semibold tracking-tight">{t("unsubscribe.title")}</h1>
        {isError ? (
          <p className="mt-2 text-[13.5px] text-danger">{t("unsubscribe.invalid")}</p>
        ) : done ? (
          <p className="mt-2 text-[13.5px] text-fg-muted">{t("unsubscribe.done", { category })}</p>
        ) : (
          <>
            <p className="mt-2 text-[13.5px] text-fg-muted">{t("unsubscribe.confirm", { category })}</p>
            <Button variant="primary" className="mt-5" loading={busy} disabled={!data} onClick={confirm}>
              {t("unsubscribe.button")}
            </Button>
          </>
        )}
        <p className="mt-5 text-[12.5px] text-fg-subtle">
          <a href="/account/notifications" className="hover:underline">
            {t("unsubscribe.manage")}
          </a>
        </p>
      </div>
    </div>
  );
}
